import React, { useMemo, useState } from 'react';
import { keyBy, sortBy } from 'lodash';
import {
    Collapsible,
    CollapsibleHeader,
    CollapsibleContent,
    CollapsibleHeaderChevron,
} from '@fiverr-private/disclosure';
import { Card, Center, Container, Divider, Stack } from '@fiverr-private/layout_components';
import { I18n } from '@fiverr-private/i18n-react';
import { SlideFade } from '@fiverr-private/transition';
import { Text } from '@fiverr-private/typography';
import { Button } from '@fiverr-private/button';
import { theme } from '@fiverr-private/theme';
import { SellerEntityTypes } from '@fiverr-private/upmarket_common';
import { Loader } from '@fiverr-private/feedback';
import { SendTemplateModal } from '../SendTemplate';
import { useTemplatesData } from '../../hooks';
import { SellerLegalDocsStats } from '../../api';
import { HandlePaymentRequest, LegalDocsConfigurations } from '../../types';
import { BiData, reportEventsApi } from '../../biEvents';
import { useResizableRef } from '../../hooks/useResizableRef';
import { SellerDocument } from './SellerDocument';
import { ListStatus } from './ListStatus/ListStatus';
import { DocumentsListEmptyState } from './DocumentsListEmptyState';

export interface SellerDocumentsListContentProps {
    sellerId: number;
    autoFetch?: boolean;
    defaultExpanded?: boolean;
    sellerName: string;
    initialData?: SellerLegalDocsStats;
    totalTemplates?: number;
    activeTemplates?: number;
    configurations?: LegalDocsConfigurations;
    handlePayment: HandlePaymentRequest;
    hideTitleDescription?: boolean;
    biData: BiData;
    sellerEntityType?: SellerEntityTypes;
}

const TRANSLATION_PREFIX = 'legal_documents_package.seller_templates';
const COMPACT_MAX_CONTECT = '130px';

export const SellerDocumentsListContent = ({
    sellerId,
    defaultExpanded = false,
    sellerName,
    initialData,
    configurations,
    activeTemplates,
    totalTemplates,
    handlePayment,
    hideTitleDescription,
    biData,
    sellerEntityType,
}: Omit<SellerDocumentsListContentProps, 'autoFetch'>) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const { sellerDataQuery, templatesQuery } = useTemplatesData({ sellerId });
    const { ref, compact } = useResizableRef();
    const { data } = sellerDataQuery;

    const documentsAvailableForSend = useMemo(() => {
        const sentTemplatesMap = keyBy(sellerDataQuery?.data || [], 'templateId');
        return templatesQuery?.data?.filter((template) => !sentTemplatesMap[template._id]);
    }, [templatesQuery?.data, sellerDataQuery?.data]);

    const canSendDocuments = !!documentsAvailableForSend?.length;

    const isLoading = !data || sellerDataQuery.isFetching || sellerDataQuery.isLoading;
    const documentsByUpdateDate = sortBy(
        data || [],
        (document) => -new Date(document.updatedAt || document.createdAt).valueOf()
    );

    const availableTemplates = useMemo(() => keyBy(templatesQuery?.data, '_id'), [templatesQuery?.data]);

    if (!data?.length && !isLoading)
        return (
            <DocumentsListEmptyState
                sellerId={sellerId}
                sellerName={sellerName}
                configurations={configurations}
                activeTemplates={activeTemplates || 0}
                totalTemplates={totalTemplates || 0}
                handlePayment={handlePayment}
                hideTitleDescription={hideTitleDescription}
                biData={biData}
                sellerEntityType={sellerEntityType}
            />
        );

    return (
        <Collapsible defaultExpanded={defaultExpanded} onToggle={setExpanded} disabled={isLoading} overflow="hidden">
            <Card borderRadius="lg" overflow="hidden" ref={ref}>
                <Container overflow="hidden">
                    <CollapsibleHeader
                        paddingLeft={compact ? '4' : '5'}
                        paddingRight={compact ? '2' : '3'}
                        paddingY={compact ? '2.5' : '3'}
                        gap={'1'}
                        textProps={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                        <Stack
                            justifyContent="spaceBetween"
                            width="100%"
                            paddingRight="2"
                            overflow="hidden"
                            flexShrink={compact ? '1' : { default: 1, sm: 0 }}
                        >
                            <Text
                                size={
                                    compact
                                        ? 'b_sm'
                                        : {
                                              default: 'b_sm',
                                              sm: 'b_md',
                                          }
                                }
                                whiteSpace="nowrap"
                                overflow="hidden"
                                textOverflow="ellipsis"
                                as="span"
                            >
                                <I18n k={`${TRANSLATION_PREFIX}.title`} />
                            </Text>
                            <SlideFade in={!expanded}>
                                <Stack alignItems="center">
                                    <ListStatus documents={data} initialData={initialData} hideDate={compact} />
                                </Stack>
                            </SlideFade>
                        </Stack>
                        <CollapsibleHeaderChevron />
                    </CollapsibleHeader>
                    {expanded && <Divider />}
                    <CollapsibleContent overflow="auto">
                        <Container>
                            <Stack
                                direction="column"
                                paddingY="4"
                                paddingX={compact ? '4' : '5'}
                                overflow="auto"
                                maxHeight={compact ? COMPACT_MAX_CONTECT : undefined}
                            >
                                {data ? (
                                    documentsByUpdateDate.map((document) => (
                                        <SellerDocument
                                            document={document}
                                            configurations={configurations}
                                            key={document._id}
                                            template={availableTemplates[document.templateId]}
                                        />
                                    ))
                                ) : (
                                    <Center width="100%">
                                        <Loader />
                                    </Center>
                                )}
                            </Stack>
                            {canSendDocuments && (
                                <>
                                    <Divider />
                                    <Stack justifyContent="flexEnd" paddingBottom="3" paddingTop="4" paddingRight="5">
                                        <SendTemplateModal
                                            biData={biData}
                                            onSend={() => sellerDataQuery.refetch()}
                                            handlePayment={handlePayment}
                                            configurations={configurations || ({} as LegalDocsConfigurations)}
                                            sellerId={sellerId}
                                            sellerName={sellerName}
                                            sellerEntityType={sellerEntityType}
                                        >
                                            <Button
                                                variant="outline"
                                                style={{ borderColor: theme.borderColors.grey_500 }}
                                                onClick={() => {
                                                    reportEventsApi.sendAnotherDocument({});
                                                }}
                                            >
                                                <Text size="b_sm" fontWeight="semibold">
                                                    <I18n k={`${TRANSLATION_PREFIX}.send_another`} />
                                                </Text>
                                            </Button>
                                        </SendTemplateModal>
                                    </Stack>
                                </>
                            )}
                        </Container>
                    </CollapsibleContent>
                </Container>
            </Card>
        </Collapsible>
    );
};

export const SellerDocumentsList = (props: SellerDocumentsListContentProps) => (
    <SellerDocumentsListContent {...props} />
);
